/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchLearnerTypes,
  updateLearnerType,
  deleteLearnerType as _deleteLearnerType,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, LearnerType, LoadStatus } from "../../../types";

export interface LearnerTypesState {
  learnerTypes: Record<string, LearnerType>;
  learnerTypeEdits: Record<string, Edits<LearnerType>>;
  learnerTypeLoadStatus: LoadStatus;
}

const initialState: LearnerTypesState = {
  learnerTypes: {},
  learnerTypeEdits: {},
  learnerTypeLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadLearnerTypes = createAsyncThunk(
  "learnerTypes/loadLearnerTypes",
  async (_, thunkAPI): Promise<LearnerType[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchLearnerTypes();
  }
);

export const addOrUpdateLearnerType = createAsyncThunk(
  "learnerTypes/addOrUpdateLearnerType",
  async (data: Partial<LearnerType>, thunkAPI): Promise<LearnerType> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await updateLearnerType(state.login.accessToken, data);
  }
);

export const deleteLearnerType = createAsyncThunk(
  "learnerTypes/deleteLearnerType",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    await _deleteLearnerType(state.login.accessToken, id);
  }
);

export const revertLearnerType = createAsyncThunk(
  "learnerTypes/revertLearnerType",
  async (id: string, thunkAPI): Promise<LearnerType> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    const edits = state.learnerTypes.learnerTypeEdits[id];
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updateLearnerType(state.login.accessToken, edits.oldData);
  }
);

export const learnerTypesSlice = createSlice({
  name: "learnerTypes",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load learnerTypes
      .addCase(loadLearnerTypes.pending, (state) => {
        state.learnerTypeLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadLearnerTypes.fulfilled, (state, action) => {
        for (const learnerType of action.payload) {
          state.learnerTypes[learnerType.id] = learnerType;
          state.learnerTypeEdits[learnerType.id] = {
            oldData: learnerType,
          };
        }
        state.learnerTypeLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadLearnerTypes.rejected, (state) => {
        state.learnerTypeLoadStatus = LoadStatus.FAILED;
      })
      // add or update learnerType
      .addCase(addOrUpdateLearnerType.fulfilled, (state, action) => {
        const learnerType = action.payload;
        if (state.learnerTypes[learnerType.id]) {
          state.learnerTypeEdits[learnerType.id].newData = learnerType;
        } else {
          state.learnerTypeEdits[learnerType.id] = {
            oldData: learnerType,
            created: true,
          };
        }
        state.learnerTypes[learnerType.id] = learnerType;
      })
      // delete learnerType
      .addCase(deleteLearnerType.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.learnerTypes[id];
      })
      // revert learnerType
      .addCase(revertLearnerType.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.learnerTypes[id] = action.payload;
        state.learnerTypeEdits[id].oldData = action.payload;
        state.learnerTypeEdits[id].newData = undefined;
      });
  },
});

export default learnerTypesSlice.reducer;
